/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * The header of a v2 record page (Account 360 and the record pages built on
 * RecordPageV2): who or what this record is at a glance. A white card washed
 * with PearlCRM blue from the top right, the navy header gradient along the
 * top, a navy-to-blue icon tile, the eyebrow and name, badges, a meta line
 * and the record's existing actions drawn as quiet outlined buttons.
 *
 * Text stays on the near-white part of the wash, so every pair keeps 4.5:1.
 */

import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { v2 as t, v2EditionAccents, V2_DISPLAY_FONT_FAMILY, V2_HEADER_GRADIENT } from "styles/tokens/v2";
import { V2PageHeaderActionsContext } from "components/MDBox";
import { v2HeaderActionsSx } from "components/v2/PageHeader";

/** PearlCRM blue, as every v2 record page uses it. */
export const BRAND_ACCENT = {
  main: v2EditionAccents.sales.accent,
  ink: v2EditionAccents.sales.ink,
  tint: alpha(v2EditionAccents.sales.accent, 0.1),
};

export function HeroBadge({ children, accent }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        height: 24,
        px: 1.1,
        borderRadius: "12px",
        fontSize: 12.5,
        fontWeight: 600,
        whiteSpace: "nowrap",
        bgcolor: accent ? accent.tint : t.surface2,
        color: accent ? accent.ink : t.text2,
        border: accent ? "none" : `1px solid ${t.border}`,
      }}
    >
      {children}
    </Box>
  );
}
HeroBadge.propTypes = { children: PropTypes.node.isRequired, accent: PropTypes.object };
HeroBadge.defaultProps = { accent: null };

function Meta({ icon: Icon, children }) {
  if (!children) return null;
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, color: t.text2, fontSize: 13.5, minWidth: 0 }}>
      <Icon size={15} aria-hidden="true" />
      <Box component="span" sx={{ whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere", lineHeight: 1.35 }}>
        {children}
      </Box>
    </Box>
  );
}
Meta.propTypes = { icon: PropTypes.elementType.isRequired, children: PropTypes.node };
Meta.defaultProps = { children: null };

export default function RecordHero({ icon: TypeIcon, eyebrow, title, badges, meta, actions, watermark: Watermark, accent, label, animate }) {
  return (
    <Box
      component="section"
      aria-label={label}
      sx={{
        position: "relative",
        overflow: "hidden",
        mb: 2.5,
        borderRadius: "12px",
        border: `1px solid ${t.border}`,
        bgcolor: t.surface,
        backgroundImage: `radial-gradient(90% 160% at 100% 0%, ${alpha(accent.main, 0.1)} 0%, ${alpha(accent.main, 0.04)} 38%, transparent 62%), linear-gradient(180deg, ${alpha(accent.main, 0.04)} 0%, transparent 70%)`,
        boxShadow: `0 1px 2px ${alpha("#0A1B3D", 0.04)}, 0 8px 24px -16px ${alpha(accent.main, 0.35)}`,
        animation: animate ? "cloneAnimation 1s ease" : "none",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: "0 0 auto 0",
          height: 3,
          background: V2_HEADER_GRADIENT,
        },
      }}
    >
      {/* Oversized icon as a watermark; decoration only. */}
      {Watermark && (
        <Box aria-hidden="true" sx={{ position: "absolute", right: -18, bottom: -34, color: alpha(accent.main, 0.06), pointerEvents: "none", display: { xs: "none", md: "block" } }}>
          <Watermark size={190} strokeWidth={1.25} />
        </Box>
      )}

      <Box sx={{ position: "relative", display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 2.25, p: { xs: 2, md: 2.75 } }}>
        <Box
          sx={{
            width: 60,
            height: 60,
            flexShrink: 0,
            borderRadius: "14px",
            display: "grid",
            placeItems: "center",
            color: "#FFFFFF",
            background: "linear-gradient(135deg, #0A1B3D 0%, #0F2E66 50%, #1565C8 100%)",
            boxShadow: `0 6px 16px -6px ${alpha("#0F2E66", 0.55)}, inset 0 1px 0 ${alpha("#FFFFFF", 0.25)}`,
          }}
        >
          <TypeIcon size={28} strokeWidth={1.75} aria-hidden="true" />
        </Box>

        <Box sx={{ flex: "1 1 320px", minWidth: 0 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: accent.ink }}>
            {eyebrow}
          </Typography>
          <Typography
            component="h1"
            sx={{ fontFamily: V2_DISPLAY_FONT_FAMILY, fontSize: { xs: 22, md: 26 }, lineHeight: 1.2, fontWeight: 600, letterSpacing: "-0.015em", color: t.text, mt: 0.25, overflowWrap: "anywhere" }}
          >
            {title || "—"}
          </Typography>

          {badges && <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1.25 }}>{badges}</Box>}

          {meta.some((m) => m.content) && (
            <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 2.25, rowGap: 0.75, mt: 1.5 }}>
              {meta.map((m) => (
                <Meta key={m.key} icon={m.icon}>
                  {m.content}
                </Meta>
              ))}
            </Box>
          )}
        </Box>

        <Box sx={{ ...v2HeaderActionsSx, ml: "auto", alignSelf: "flex-start" }}>
          <V2PageHeaderActionsContext.Provider value>{actions}</V2PageHeaderActionsContext.Provider>
        </Box>
      </Box>
    </Box>
  );
}

RecordHero.propTypes = {
  icon: PropTypes.elementType.isRequired,
  eyebrow: PropTypes.node.isRequired,
  title: PropTypes.node,
  badges: PropTypes.node,
  meta: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string.isRequired, icon: PropTypes.elementType.isRequired, content: PropTypes.node })),
  actions: PropTypes.node,
  watermark: PropTypes.elementType,
  accent: PropTypes.shape({ main: PropTypes.string, ink: PropTypes.string, tint: PropTypes.string }),
  label: PropTypes.string,
  // The record page's clone flash (cloneAnimation), as the classic header has.
  animate: PropTypes.bool,
};

RecordHero.defaultProps = {
  title: null,
  badges: null,
  meta: [],
  actions: null,
  watermark: null,
  accent: BRAND_ACCENT,
  label: "Record summary",
  animate: false,
};
